// Document domain actions extracted from useAppState.js.
// Handles all document CRUD, version snapshots, numbering, and workflow transitions.
import { useCallback } from 'react';

function dispatchWriteError() {
  try {
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('seasuite:write-error'));
    }
  } catch {
    /* non-browser — no-op */
  }
}
import { flushSync } from 'react-dom';
import { generateId, now } from '../utils/formatting.js';
import { diffDocumentVersions } from '../utils/diffDocument.js';
import { logActivity } from '../api/activityLogger.js';
import { emitEngineEvent } from '../engine/engineEventBus.js';
import { logDiag } from '../diagnostics/diagnosticsClient.js';
import { saveDocumentPatch, deleteDocumentRow, callDocumentWorkflow } from '../api/tenantApi.js';
import { uploadDocumentVersionSnapshot } from '../api/storage-azure.js';
import { recordOwnDocVersion } from '../documents/ownDocVersions.js';
import {
  DOC_STATUS,
  normalizeDocStatus,
  getDocRoles,
  reviewersQuorumSatisfied,
  reviewerCanDecide,
  documentContentHash,
  canTransition,
  independentReviewSatisfied,
  bumpMinor,
  bumpMajor,
  makeHistoryEntry,
  isConcurrentDocChangeConflict,
} from '../utils/documentWorkflow.js';
import {
  generateDocumentNumber,
  claimSequence,
  parseDocumentNumber,
} from '../utils/documentNumbering.js';
import { adoptTenantStateVersion } from '../api/tenantStateVersion.js';

const MAX_VERSION_HISTORY = 25;
const KEEP_SNAPSHOTS = 3;

/**
 * Cap a document's version history without ever losing the approved version
 * (#6964, DOC-B-015). The module's doneLooksLike is "a user can identify the
 * approved version, approver, effective date and change history" — a plain
 * newest-25 slice dropped the approved entry after 25 saves.
 *
 * An entry is PINNED when it carries an approvalRecord (written by
 * start_new_version), records a discarded revision (discard_revision), or its
 * version equals any approved or published version.
 * Pinned entries are never trimmed and keep their inline contentSnapshot; the
 * cap and the snapshot thinning apply to the rest exactly as before. Pure, so
 * it is tested directly.
 *
 * Known next layer (named, not fixed here): pinned inline snapshots live in
 * the document row and grow with every approval cycle; they belong in the
 * `_versions/` blob store rather than the row.
 */
export function trimVersionHistory(history, { approvedVersions = [] } = {}) {
  const list = Array.isArray(history) ? history : [];
  const approved = new Set(approvedVersions.filter(Boolean));
  for (const entry of list) {
    if (entry?.approvalRecord?.version) approved.add(entry.approvalRecord.version);
  }
  // A discarded revision's entry (CDEB-01B) is pinned too: it is the only
  // record of work that was started and then withdrawn. So is an entry that
  // carries a body it replaced wholesale (opts.replacesBody): that copy is the
  // only way back to the text the user was warned they would lose.
  const isPinned = (entry) =>
    !!entry?.approvalRecord ||
    !!entry?.discardedRevision ||
    !!entry?.replacedBody ||
    approved.has(entry?.version);

  const unpinned = [];
  list.forEach((entry, i) => {
    if (!isPinned(entry)) unpinned.push(i);
  });
  const dropped = new Set(unpinned.slice(0, Math.max(0, unpinned.length - MAX_VERSION_HISTORY)));
  const kept = list.filter((_, i) => !dropped.has(i));

  const cutoff = kept.length - KEEP_SNAPSHOTS;
  if (cutoff <= 0) return kept;
  return kept.map((entry, i) => {
    if (i >= cutoff || !entry?.contentSnapshot || isPinned(entry)) return entry;
    const { contentSnapshot: _drop, ...rest } = entry;
    return rest;
  });
}

export function useDocumentState({
  updateState,
  tenantId,
  stateRef,
  adoptStateVersion: _adoptStateVersion, // GH #7714: version adoption goes through adoptTenantStateVersion
  beginExternalStateWrite,
}) {
  // Every write below can bump tenant_state's row version — documents-write
  // patches the document-number and AI-training slices into the state blob, and
  // documents-workflow rewrites it outright on a status transition. Each one is
  // triggered by a user action that also dirties local state, arming the
  // debounced full-state save. Adopting the new version when the call returns
  // is necessary but not sufficient: the debounce is 1.5s and these calls
  // regularly take longer, so the save fires with the superseded version and is
  // refused — reported to a lone user as "Workspace updated elsewhere"
  // (GH #5314). Claiming the version for the duration of the call closes that
  // window; the release must run even when the write throws, or the debounced
  // save is held until the deferral ceiling expires.
  const runVersionBumpingWrite = useCallback(
    async (writeFn) => {
      const release = beginExternalStateWrite ? beginExternalStateWrite() : null;
      try {
        const result = await writeFn();
        // GH #7714 (co-owner round 3): through the same check as the transport —
        // adopted only when the write read the version this tab holds, never on
        // a missing stamp or a broken chain. A bare adoptStateVersion here
        // re-adopted the version azureFetch had just (correctly) declined.
        adoptTenantStateVersion(result);
        return result;
      } finally {
        if (release) release();
      }
    },
    [beginExternalStateWrite]
  );

  // ── Adopting what a documents-workflow transition produced ────────────────
  // A transition writes the document row, so it produces a new per-record row
  // version exactly like a content save does. documents-workflow returns it
  // (stamped onto the returned doc as `_docVersion`, and separately as
  // `docVersion`); every transition path must adopt it, or the next content
  // save from an editor still open on this document sends a token the server
  // has already moved past and is refused a 409 the user did not cause — the
  // "which version do you want to keep?" prompt on a document nobody edited
  // (GH #6672).
  //
  // recordOwnDocVersion's invariant (ownDocVersions.js) is satisfied: this
  // version was RETURNED BY a write THIS tab issued, and the merge below makes
  // local state hold exactly the row that write produced — so "the row is on a
  // version I wrote" still also means "the row's content is a subset of my
  // local state". It is emphatically NOT a version learned from someone else's
  // response.
  const adoptWorkflowDoc = useCallback(
    (docId, serverDoc) => {
      if (!docId || !serverDoc) return null;
      if (serverDoc._docVersion) recordOwnDocVersion(docId, serverDoc._docVersion);
      let merged = null;
      updateState((s) => {
        const d = s.documents.find((x) => x.id === docId);
        if (d) {
          Object.assign(d, serverDoc);
          // The workflow returns the whole document, so a key it no longer
          // carries was removed server-side. Object.assign cannot express a
          // removal: without this a discarded revision (CDEB-01B
          // discard_revision) would linger in local state and reopen.
          if (!Object.prototype.hasOwnProperty.call(serverDoc, 'draftRevision')) {
            delete d.draftRevision;
          }
          merged = { ...d };
        }
      });
      return merged;
    },
    [updateState]
  );

  /**
   * Save a document.
   *
   * Note the argument list callers actually see. Almost nothing holds this
   * function directly — components get `saveDocumentLogged` from App.jsx, which
   * injects `session` itself and so takes `opts` at position FIVE, where this
   * one takes `session`. Pass `session` at position 5 through the wrapper and
   * it silently lands in `opts`: no error anywhere, `opts.isRetry` merely
   * undefined, and the phantom-revision bug below comes back.
   *
   * `opts.isRetry` means one specific thing: **a re-issue of the immediately
   * preceding failed call, carrying an identical payload, set by a retry loop
   * and by nothing else.** It tells this function the version-history entry for
   * this save already exists. Setting it on a call that is not that — two
   * genuine saves that happen to share a version label, which the workflow hub
   * and the review modal both make (they save without bumping `version`) —
   * would collapse a real revision into the previous one and lose it.
   */
  const saveDocument = useCallback(
    async (doc, changeNote, currentUserId, currentUserName, session, opts = {}) => {
      let isEdit = false;
      let priorDoc = null;
      let savedStamp = null;
      let sequencesTouched = false;
      let trainingPairsTouched = false;
      flushSync(() => {
        updateState((s) => {
          const idx = s.documents.findIndex((d) => d.id === doc.id);
          isEdit = idx >= 0;
          const prev = isEdit ? s.documents[idx] : null;
          const ts = now();
          priorDoc = prev;
          savedStamp = ts;

          const changes = prev ? diffDocumentVersions(prev, doc) : ['Document created'];
          if (changeNote) changes.unshift(`Note: ${changeNote}`);

          const contentSnapshot = {
            title: doc.title,
            content: (doc.content || []).map((b) =>
              b.type === 'image' ? { ...b, src: '[snapshot]' } : b
            ),
            controls: doc.controls || [],
            description: doc.description || '',
            category: doc.category || '',
            status: doc.status || '',
            documentStatus: doc.documentStatus || 'draft',
          };

          const historyEntry = {
            // Names this entry's snapshot blob, so two entries sharing a
            // version label never share (and overwrite) one (GH #8800).
            entryId: generateId(),
            version: doc.version,
            updatedAt: ts,
            updatedBy: currentUserId,
            changes,
            contentSnapshot,
          };
          if (doc._materiality) {
            historyEntry.materiality = doc._materiality;
            if (doc._materialityJustification)
              historyEntry.materialityJustification = doc._materialityJustification;
          }
          // A save that replaces the whole body (a regenerated AI draft) keeps
          // the body it replaced on its own entry, pinned by
          // trimVersionHistory. The entry's contentSnapshot is the NEW body;
          // the previous entry's snapshot is thinned after KEEP_SNAPSHOTS
          // saves, so without this the replaced text would not survive.
          if (opts.replacesBody && prev) {
            historyEntry.replacedBody = {
              reason: String(opts.replacesBody),
              contentSnapshot: {
                title: prev.title,
                content: (prev.content || []).map((b) =>
                  b.type === 'image' ? { ...b, src: '[snapshot]' } : b
                ),
              },
            };
          }
          if (doc._reviewSkipped) {
            historyEntry.reviewSkipped = true;
            if (doc._reviewSkipJustification)
              historyEntry.reviewSkipJustification = doc._reviewSkipJustification;
          }
          // GH #6303: saveDocument is called again — not resumed — every time
          // DocumentEditor's save loop retries a write the server refused as a
          // false conflict, and this append runs inside flushSync BEFORE the
          // network call, so the refused attempt's entry has already committed
          // by the time the retry arrives. Appending again persists a second
          // revision carrying the same version label, an empty change list
          // (prev is now the first attempt's own result) and its own snapshot.
          // Version history is what an approver reads to decide what changed
          // (DocumentApprovalModal), so a phantom revision is a record of an
          // edit that never happened, in front of the person being asked to
          // approve it. A retry re-issues the payload it already built; the
          // entry that payload produced is already here and already correct.
          const lastEntry = prev?.versionHistory?.[prev.versionHistory.length - 1];
          const isRetryOfLastEntry =
            !!opts.isRetry &&
            !!lastEntry &&
            lastEntry.version === doc.version &&
            lastEntry.updatedBy === currentUserId;
          // CDEB-01B: a save of a draft revision held beside the published row
          // edits only doc.draftRevision. It is not a version of the document
          // in force, so it appends no versionHistory entry and uploads no
          // snapshot of the published body (opts.revisionSave, set by
          // DocumentEditorEntry's revision host).
          let versionHistory = opts.revisionSave
            ? [...(prev?.versionHistory || [])]
            : isRetryOfLastEntry
              ? [...prev.versionHistory]
              : [...(prev?.versionHistory || []), historyEntry];

          versionHistory = trimVersionHistory(versionHistory, {
            approvedVersions: [
              prev?.approvalRecord?.version,
              doc.approvalRecord?.version,
              prev?.publishedVersion,
              doc.publishedVersion,
            ],
          });

          const {
            _materiality,
            _materialityJustification,
            _reviewSkipped,
            _reviewSkipJustification,
            ...docFields
          } = doc;
          // Merge over the previously stored doc: callers (the editor, the
          // workflow hub) send a whitelist payload, not the full document.
          // Without the merge, every save silently wipes fields the caller
          // doesn't carry — documentNumber (then re-issued as a NEW number
          // below, burning a sequence per save), stateHistory, reviewDecisions,
          // publishedAt/publishedVersion/everPublished, approvalRequired,
          // createdAt, and the engine-owned reviewRemindersLedger/renewalTaskId
          // (DOC-B-004 dedup ledger). Keys the caller explicitly sets (even to
          // null) still win; undefined means "not provided", never a deletion.
          for (const k of Object.keys(docFields)) {
            if (docFields[k] === undefined) delete docFields[k];
          }
          const savedDoc = { ...(prev || {}), ...docFields, lastUpdated: ts, versionHistory };
          if (!isEdit && !savedDoc.createdAt) savedDoc.createdAt = ts;

          const prevTypeId = prev?.documentTypeId || prev?.category || '';
          const newTypeId = savedDoc.documentTypeId || savedDoc.category || '';
          const typeChanged = isEdit && prevTypeId && newTypeId && prevTypeId !== newTypeId;

          if (savedDoc.documentTypeId && (!isEdit || typeChanged || !savedDoc.documentNumber)) {
            const issued = generateDocumentNumber({
              typeId: savedDoc.documentTypeId,
              documentTypes: s.documentTypes || [],
              documents: s.documents,
              deletedDocuments: s.deletedDocuments || [],
              sequenceRegistry: s.documentNumberSequences || {},
            });
            savedDoc.documentNumber = issued.number;
            savedDoc.sequenceNumber = issued.sequence;
            savedDoc.level = issued.level;
            s.documentNumberSequences = claimSequence(
              s.documentNumberSequences || {},
              issued.abbreviation,
              issued.sequence
            );
            sequencesTouched = true;
          } else if (savedDoc.documentNumber) {
            const parsed = parseDocumentNumber(savedDoc.documentNumber);
            if (parsed) {
              const before = s.documentNumberSequences || {};
              const after = claimSequence(before, parsed.abbreviation, parsed.sequence);
              if (after !== before) sequencesTouched = true;
              s.documentNumberSequences = after;
            }
          }

          if (isEdit) {
            s.documents[idx] = savedDoc;
          } else {
            s.documents = [savedDoc, ...s.documents];
          }

          const controls = doc.controls || [];
          const blocks = doc.content || [];
          if (s.aiLearningEnabled && controls.length > 0 && blocks.length > 0) {
            const plainText = blocks
              .filter((b) => b.text && b.type !== 'image' && b.type !== 'pagebreak')
              .map((b) => b.text.replace(/<[^>]+>/g, '').trim())
              .filter(Boolean)
              .join('\n');

            if (plainText.length > 20) {
              const pairs = s.aiTrainingPairs || [];
              const existing = pairs.findIndex((p) => p.docId === doc.id);
              const pair = {
                id: existing >= 0 ? pairs[existing].id : generateId(),
                docId: doc.id,
                docTitle: doc.title,
                text: plainText,
                controls: controls
                  .map((c) => (typeof c === 'string' ? c : c.controlId))
                  .filter(Boolean),
                createdAt: ts,
              };
              if (existing >= 0) {
                s.aiTrainingPairs = pairs.map((p, i) => (i === existing ? pair : p));
              } else {
                s.aiTrainingPairs = [...pairs, pair];
              }
              trainingPairsTouched = true;
            }
          }
        });
      });

      const savedDoc = stateRef.current.documents.find((d) => d.id === doc.id);
      if (!savedDoc) {
        throw new Error('Save failed: document missing from state after mutation');
      }

      const latestEntry = savedDoc.versionHistory?.[savedDoc.versionHistory.length - 1];
      // Started only once the write has been accepted: a refused save (a 409,
      // a lost connection) must leave no snapshot of text that was never saved.
      const startSnapshotUpload = () =>
        !opts.revisionSave && latestEntry?.contentSnapshot && latestEntry?.version != null
          ? uploadDocumentVersionSnapshot(
              doc.id,
              latestEntry.version,
              latestEntry.contentSnapshot,
              latestEntry.entryId
            ).catch((e) => {
              console.warn('[useDocumentState] version snapshot upload failed:', e?.message || e);
              return null;
            })
          : Promise.resolve(null);

      const payload = { doc: savedDoc };
      if (sequencesTouched) {
        payload.documentNumberSequences = stateRef.current.documentNumberSequences || {};
      }
      if (trainingPairsTouched) {
        payload.aiTrainingPairs = stateRef.current.aiTrainingPairs || [];
      }

      const startedAt = Date.now();
      let saveResult;
      try {
        saveResult = await runVersionBumpingWrite(() => saveDocumentPatch(tenantId, payload));
        logDiag('doc.persist', { docId: doc.id, durationMs: Date.now() - startedAt, ok: true });
      } catch (e) {
        logDiag('doc.persist', {
          docId: doc.id,
          durationMs: Date.now() - startedAt,
          ok: false,
          errorMessage: String(e?.message || e).slice(0, 200),
        });
        // A wholesale body replacement that the server refused must not stay
        // on screen as if it were the document. Put back what the server
        // holds — its current copy on a version conflict, otherwise the
        // document exactly as it was before this save — but only while state
        // still holds this save's own copy, so a newer one is never replaced.
        if (opts.replacesBody && priorDoc) {
          const serverDoc = e?.serverDoc?.id === doc.id ? e.serverDoc : null;
          updateState((s) => {
            const i = s.documents.findIndex((d) => d.id === doc.id);
            if (i < 0 || s.documents[i].lastUpdated !== savedStamp) return;
            s.documents[i] = serverDoc || priorDoc;
          });
        }
        throw e;
      }
      const snapshotUpload = startSnapshotUpload();

      // Stamp the row version the write just produced back onto the doc in
      // state BEFORE the snapshotBlob follow-up reads it from stateRef.
      // Without this the follow-up resends the pre-save _docVersion, which the
      // server's optimistic-concurrency guard rejects as a 409 — a self-
      // inflicted conflict, not a real one (see documents-write ~186-202).
      if (saveResult?.docVersion) {
        // GH #6303: also remember the token as one THIS tab produced. The
        // editor's pre-save refresh and its 409 handler both have to decide
        // whether a version they did not expect came from another writer, and
        // content cannot answer that (ownDocVersions.js).
        recordOwnDocVersion(doc.id, saveResult.docVersion);
        updateState((s) => {
          const i = s.documents.findIndex((d) => d.id === doc.id);
          if (i < 0) return;
          s.documents[i] = { ...s.documents[i], _docVersion: saveResult.docVersion };
        });
      }

      // GH #7952: on create the server stores the document without the fields
      // it owns (approval evidence) and names the ones it removed. Drop them
      // here too, so this copy matches the stored row — otherwise the
      // snapshotBlob follow-up below (and every later save) re-sends them and
      // documents-write refuses the write as a change to a server-owned field.
      if (Array.isArray(saveResult?.strippedFields) && saveResult.strippedFields.length) {
        const stripped = saveResult.strippedFields;
        updateState((s) => {
          const i = s.documents.findIndex((d) => d.id === doc.id);
          if (i < 0) return;
          const next = { ...s.documents[i] };
          for (const k of stripped) delete next[k];
          s.documents[i] = next;
        });
      }

      snapshotUpload.then((snapshotBlob) => {
        if (!snapshotBlob) return;
        updateState((s) => {
          const i = s.documents.findIndex((d) => d.id === doc.id);
          if (i < 0) return;
          const hist = s.documents[i].versionHistory || [];
          const lastIdx = hist.length - 1;
          if (lastIdx < 0) return;
          const last = hist[lastIdx];
          // The blob belongs to the entry it was uploaded for. Matching on the
          // version label alone would hand it to a newer save of the same label.
          if (
            last.version !== latestEntry.version ||
            last.entryId !== latestEntry.entryId ||
            last.snapshotBlob
          ) {
            return;
          }
          const patched = { ...last, snapshotBlob };
          const newHistory = [...hist.slice(0, lastIdx), patched];
          s.documents[i] = { ...s.documents[i], versionHistory: newHistory };
        });
        const updated = stateRef.current.documents.find((d) => d.id === doc.id);
        if (updated) {
          saveDocumentPatch(tenantId, { doc: updated })
            .then((patchResult) => {
              // Stamp the version THIS write produced (mirrors the first
              // write's stamp above, lines ~243-249). Without it the client's
              // cached _docVersion stays one write behind the server's real
              // row version after every save — the next save (in this
              // session, or after a remount that re-seeds docVersionRef from
              // this cached value) sends a stale version and gets a spurious
              // 409 on an otherwise-unrelated edit (issue #4889).
              if (patchResult?.docVersion) {
                // GH #6303: this is the bump the editor cannot otherwise
                // account for — it lands in the background AFTER the
                // foreground save already returned, so from the editor's side
                // it is indistinguishable from a stranger's write. Recording
                // it as ours is what lets the next save adopt it instead of
                // self-conflicting.
                recordOwnDocVersion(doc.id, patchResult.docVersion);
                updateState((s) => {
                  const j = s.documents.findIndex((d) => d.id === doc.id);
                  if (j < 0) return;
                  s.documents[j] = { ...s.documents[j], _docVersion: patchResult.docVersion };
                });
              }
            })
            .catch((e) => {
              // DOC-B-001: a documents-workflow transition (e.g. "Submit for
              // Review") can legitimately advance the document between when
              // `updated` was read above and when the server processes this
              // request — documents-write then rejects it, as a 409 write
              // conflict or (when the version happens to still match) the
              // status-immutability message. The transition itself already
              // succeeded and persisted; this write only carries the version-
              // snapshot blob ref and no user content, so nothing is lost.
              // Treat it as a benign no-op rather than alarming the user with
              // a false "write failed" banner (issue #3162) — any other
              // failure still does.
              if (isConcurrentDocChangeConflict(e)) {
                console.warn(
                  '[useDocumentState] snapshotBlob ref persist skipped — document status changed concurrently:',
                  e.message
                );
                // Benign for THIS write, but not for the next one. documents-write
                // stamps the current row version onto `serverDoc` on a 409
                // precisely so the client can resync without a second round-trip
                // — and dropping it left `_docVersion` in local state one write
                // behind the server. The user's next genuine save then sent a
                // stale version and was refused with a conflict nobody caused:
                // a real edit blocked by the fallout of a write we had just
                // decided did not matter (#5357, and the live "[Auto] Write
                // conflict — please retry" reports in #5352).
                // Deliberately NOT recorded via recordOwnDocVersion: this
                // token came off a server response, not off a write of ours,
                // so it may well be another writer's. The identity registry is
                // only ever fed versions our own writes RETURNED — the moment a
                // foreign version is recorded as own, the editor stops raising a
                // spurious dialog and starts silently overwriting the writer who
                // produced it, which is GH #6240 all over again (ownDocVersions.js).
                const serverVersion = e?.serverDoc?._docVersion;
                if (serverVersion) {
                  updateState((s) => {
                    const j = s.documents.findIndex((d) => d.id === doc.id);
                    if (j < 0) return;
                    s.documents[j] = { ...s.documents[j], _docVersion: serverVersion };
                  });
                }
                return;
              }
              console.warn('[useDocumentState] snapshotBlob ref persist failed:', e?.message || e);
              dispatchWriteError();
            });
        }
      });

      emitEngineEvent('document.saved', { doc });

      if (session) {
        logActivity(tenantId, session, {
          module: 'Documents',
          actionType: isEdit ? 'edited' : 'created',
          recordId: doc.id,
          recordLabel: doc.title,
        });
      }

      return { docVersion: saveResult?.docVersion || null };
    },
    [updateState, tenantId, stateRef, runVersionBumpingWrite]
  );

  const deleteDocument = useCallback(
    async (id, label, session) => {
      // Server-first (DOC-B-003): documents-delete enforces the soft-delete
      // state guard (ERR_WRONG_STATE for a document still in_review /
      // ready_for_approval / approved / published) and is itself the
      // authoritative write to state.documents / state.deletedDocuments. Local
      // state must only move the document into the deletedDocuments bucket
      // once the server has actually accepted the delete — mutating first let
      // the app's own debounced state-write persist a removal the server had
      // just refused, so the document vanished from the UI with no tombstone,
      // unrestorable, and with no activity-log entry.
      //
      // A refusal is rethrown, never swallowed: the confirm modal shows it.
      // Swallowing it closed the modal on a delete that never happened — the
      // document simply stayed, with nothing telling the user why (a wrong
      // state, or someone else saving it in the meantime, GH #6094 / #6960).
      await runVersionBumpingWrite(() => deleteDocumentRow(tenantId, id));

      // documents-delete writes state.deletedDocuments/state.documents directly
      // (server-first, DB-CONC-001 versioned write) and bumps tenant_state's
      // row version — runVersionBumpingWrite above both holds the debounced
      // save off for the duration and adopts the new version on return. Without
      // that, the next debounced state-write sends the pre-delete version and
      // self-conflicts with a guaranteed 409 — the "Workspace updated
      // elsewhere" banner firing on every delete, not just a genuine
      // concurrent edit.

      let movedDoc = null;
      updateState((s) => {
        const idx = s.documents.findIndex((d) => d.id === id);
        if (idx === -1) return;
        const doc = s.documents[idx];
        movedDoc = {
          ...doc,
          deletedAt: now(),
          deletedBy: session?.userId || null,
        };
        s.documents = s.documents.filter((d) => d.id !== id);
        if (!Array.isArray(s.deletedDocuments)) s.deletedDocuments = [];
        s.deletedDocuments.push(movedDoc);
      });

      if (!movedDoc) return;

      emitEngineEvent('document.deleted', { docId: id });

      if (session) {
        logActivity(tenantId, session, {
          module: 'Documents',
          actionType: 'deleted',
          recordId: id,
          recordLabel: label || movedDoc.title || id,
        });
      }
    },
    [updateState, tenantId, runVersionBumpingWrite]
  );

  const retractDocument = useCallback(
    (docId, session) => {
      let retractedDoc = null;
      updateState((s) => {
        const doc = s.documents.find((d) => d.id === docId);
        if (!doc || doc.documentStatus !== 'to_review') return;
        const ts = now();
        doc.documentStatus = 'draft';
        doc.approverId = null;
        doc.approvalRequestedAt = null;
        doc.approvalDecision = null;
        doc.approvalComment = null;
        doc.approvalDecidedAt = null;
        doc.lastUpdated = ts;
        doc.versionHistory = [
          ...(doc.versionHistory || []),
          {
            version: doc.version,
            updatedAt: ts,
            updatedBy: session?.userId,
            changes: ['Retracted from review'],
          },
        ];
        retractedDoc = { ...doc };
      });

      if (retractedDoc) emitEngineEvent('document.retracted', { doc: retractedDoc });

      if (session && retractedDoc) {
        logActivity(tenantId, session, {
          module: 'Documents',
          actionType: 'retracted',
          recordId: docId,
          recordLabel: retractedDoc.title,
        });
      }
    },
    [updateState, tenantId]
  );

  // DOC-B-001: every documentStatus transition must go through the
  // documents-workflow Azure Function. state-read overlays tenant_documents
  // rows over the state blob (table wins), so a local-only status change
  // would silently revert on reload.
  const saveDocumentApproval = useCallback(
    async (docId, decision, comment, session) => {
      const doc = stateRef.current?.documents?.find((d) => d.id === docId);
      if (!doc) throw new Error('Document not found.');

      const action = decision === 'approved' ? 'approve' : 'reject';
      const verb = action === 'approve' ? 'approve' : 'reject';
      const rule = action === 'approve' ? 'approve' : 'reject_by_owner';
      if (!canTransition(doc, rule, session?.userId)) {
        const status = normalizeDocStatus(doc.documentStatus);
        if (status !== DOC_STATUS.READY_FOR_APPROVAL) {
          throw new Error(
            `Cannot ${verb} this document: it is "${status.replace(/_/g, ' ')}", not ready for approval. ` +
              'Submit it through the review workflow first.'
          );
        }
        const { isOwner, isWriter } = getDocRoles(doc, session?.userId);
        if (action === 'approve' && isOwner && isWriter) {
          throw new Error(
            'Cannot approve this document: the owner cannot approve a document they also wrote.'
          );
        }
        throw new Error(`Only the document owner can ${verb} this document.`);
      }

      // #6964: an approval binds to the exact text this client holds and shows.
      const contentHash = action === 'approve' ? await documentContentHash(doc) : undefined;
      let result;
      try {
        result = await runVersionBumpingWrite(() =>
          callDocumentWorkflow(tenantId, action, {
            docId,
            comment: comment || null,
            ...(contentHash ? { contentHash } : {}),
          })
        );
      } catch (err) {
        const code = err?.message;
        if (code === 'ERR_CONTENT_CHANGED') {
          throw new Error(
            'The text stored for this document is not the text you were shown, so the approval was not recorded. Close and reopen the document, read it again, then approve.'
          );
        }
        if (code === 'ERR_CONTENT_HASH_REQUIRED') {
          throw new Error('Please reload the page, reopen the document, then approve.');
        }
        throw err;
      }
      const serverDoc = result?.doc;
      if (!serverDoc) throw new Error('The workflow service returned no document.');

      const approvedDoc = adoptWorkflowDoc(docId, serverDoc);

      if (approvedDoc) emitEngineEvent('document.approved', { doc: approvedDoc, decision });

      if (session && approvedDoc) {
        logActivity(tenantId, session, {
          module: 'Documents',
          actionType: `approved_${decision}`,
          recordId: docId,
          recordLabel: approvedDoc.title,
        });
      }
    },
    [adoptWorkflowDoc, tenantId, stateRef, runVersionBumpingWrite]
  );

  // Reassigning the Approver (doc.ownerId) is admin-gated and must be
  // enforced server-side (DOC-B-006) — it goes through documents-workflow's
  // reassign_owner action so the compound reset (clearing reviewDecisions/
  // approvalRecord only when the document is genuinely mid-review) and the
  // audit trail (ownerReassignmentLog) stay authoritative, never a local-only
  // state mutation.
  const reassignDocumentOwner = useCallback(
    async (docId, newOwnerId, comment, session) => {
      const isAdmin = session?.role === 'admin' || session?.role === 'super-admin';
      if (!isAdmin) throw new Error('Only an admin can reassign the approver.');

      const result = await runVersionBumpingWrite(() =>
        callDocumentWorkflow(tenantId, 'reassign_owner', {
          docId,
          newOwnerId,
          comment: comment || null,
        })
      );
      const serverDoc = result?.doc;
      if (!serverDoc) throw new Error('The workflow service returned no document.');

      const reassignedDoc = adoptWorkflowDoc(docId, serverDoc);

      if (reassignedDoc) emitEngineEvent('document.owner_reassigned', { doc: reassignedDoc });

      if (session && reassignedDoc) {
        logActivity(tenantId, session, {
          module: 'Documents',
          actionType: 'owner_reassigned',
          recordId: docId,
          recordLabel: reassignedDoc.title,
        });
      }
      return reassignedDoc;
    },
    [adoptWorkflowDoc, tenantId, runVersionBumpingWrite]
  );

  const submitDocumentForReview = useCallback(
    (docId, { reviewers = [], noReviewersJustification = null } = {}, session) => {
      let result = null;
      updateState((s) => {
        const doc = s.documents.find((d) => d.id === docId);
        if (!doc) return;
        if (!canTransition(doc, 'submit_for_review', session?.userId)) return;

        const ts = now();
        const hasReviewers = reviewers.length > 0;

        doc.documentStatus = DOC_STATUS.IN_REVIEW;
        doc.reviewers = hasReviewers ? reviewers : doc.reviewers || [];
        doc.reviewDecisions = [];
        doc.reviewerEscalations = [];
        doc.reviewerOverride = null;
        doc.reviewCycleStartedAt = ts;
        doc.lastUpdated = ts;
        if (!doc.approvalRequiredLockedAt) doc.approvalRequiredLockedAt = ts;
        doc.approverId = doc.reviewers[0]?.userId || null;
        doc.approvalRequestedAt = ts;
        doc.approvalDecision = null;

        const histEntry = makeHistoryEntry({
          from: DOC_STATUS.DRAFT,
          to: DOC_STATUS.IN_REVIEW,
          by: session?.userId,
          reason: !hasReviewers ? 'no-reviewers-justification' : null,
          comment: !hasReviewers ? noReviewersJustification : null,
          extra: !hasReviewers
            ? { reviewSkipped: true, reviewSkipJustification: noReviewersJustification }
            : {},
        });
        doc.stateHistory = [...(doc.stateHistory || []), histEntry];
        doc.versionHistory = [
          ...(doc.versionHistory || []),
          {
            version: doc.version,
            updatedAt: ts,
            updatedBy: session?.userId,
            changes: ['Submitted for review'],
            reviewSkipped: !hasReviewers,
            reviewSkipJustification: !hasReviewers ? noReviewersJustification : null,
          },
        ];
        result = { ...doc };
      });

      if (result) {
        emitEngineEvent('document.submitted_for_review', { doc: result });
        if (session)
          logActivity(tenantId, session, {
            module: 'Documents',
            actionType: 'submitted_for_review',
            recordId: docId,
            recordLabel: result.title,
          });
      }
    },
    [updateState, tenantId]
  );

  const submitReviewerDecision = useCallback(
    (docId, decision, comment, session) => {
      let result = null;
      let err = null;
      let autoAdvanced = false;
      updateState((s) => {
        const doc = s.documents.find((d) => d.id === docId);
        if (!doc) return;
        if (!canTransition(doc, 'submit_reviewer_decision', session?.userId)) {
          err = !reviewerCanDecide(doc, session?.userId)
            ? 'ERR_DECISION_ALREADY_SUBMITTED'
            : 'ERR_FORBIDDEN';
          return;
        }
        if (decision === 'changes-requested' && !comment?.trim()) {
          err = 'ERR_COMMENT_REQUIRED';
          return;
        }

        const ts = now();
        doc.reviewDecisions = [
          ...(doc.reviewDecisions || []),
          {
            reviewerId: session?.userId,
            decision,
            decidedAt: ts,
            comment: comment || null,
          },
        ];
        doc.lastUpdated = ts;

        if (decision === 'changes-requested') {
          doc.documentStatus = DOC_STATUS.CHANGES_REQUESTED;
          doc.stateHistory = [
            ...(doc.stateHistory || []),
            makeHistoryEntry({
              from: DOC_STATUS.IN_REVIEW,
              to: DOC_STATUS.CHANGES_REQUESTED,
              by: session?.userId,
              comment,
              reason: 'reviewer-changes-requested',
            }),
          ];
        } else if (reviewersQuorumSatisfied(doc)) {
          // This 'ok' completes reviewer quorum — advance to approval
          // automatically. The writer no longer performs a manual "advance"
          // step. The override / no-reviewers paths still go through
          // advanceToReadyForApproval, which requires a justification comment.
          doc.documentStatus = DOC_STATUS.READY_FOR_APPROVAL;
          doc.stateHistory = [
            ...(doc.stateHistory || []),
            makeHistoryEntry({
              from: DOC_STATUS.IN_REVIEW,
              to: DOC_STATUS.READY_FOR_APPROVAL,
              by: session?.userId,
              reason: 'quorum-met-auto-advance',
            }),
          ];
          autoAdvanced = true;
        }
        result = { ...doc };
      });

      if (err) return err;
      if (result) {
        emitEngineEvent('document.reviewer_decision', { doc: result, decision });
        if (autoAdvanced) emitEngineEvent('document.ready_for_approval', { doc: result });
        if (session) {
          logActivity(tenantId, session, {
            module: 'Documents',
            actionType: `reviewer_${decision}`,
            recordId: docId,
            recordLabel: result.title,
          });
          if (autoAdvanced)
            logActivity(tenantId, session, {
              module: 'Documents',
              actionType: 'ready_for_approval',
              recordId: docId,
              recordLabel: result.title,
            });
        }
      }
    },
    [updateState, tenantId]
  );

  const advanceToReadyForApproval = useCallback(
    (docId, overrideComment, session) => {
      let result = null;
      let err = null;
      updateState((s) => {
        const doc = s.documents.find((d) => d.id === docId);
        if (!doc) return;
        const { isWriter } = getDocRoles(doc, session?.userId);
        if (!isWriter) {
          err = 'ERR_FORBIDDEN';
          return;
        }
        if (normalizeDocStatus(doc.documentStatus) !== DOC_STATUS.IN_REVIEW) {
          err = 'ERR_WRONG_STATE';
          return;
        }

        const quorumMet = reviewersQuorumSatisfied(doc);
        const noReviewers = (doc.reviewers || []).length === 0;
        const needsOverride = !quorumMet && !noReviewers;

        if (needsOverride && !overrideComment?.trim()) {
          err = 'ERR_COMMENT_REQUIRED';
          return;
        }

        const ts = now();
        doc.documentStatus = DOC_STATUS.READY_FOR_APPROVAL;
        doc.lastUpdated = ts;

        if (needsOverride || noReviewers) {
          doc.reviewerOverride = {
            overriddenBy: session?.userId,
            overriddenAt: ts,
            comment: overrideComment,
          };
        }

        const reason = needsOverride
          ? 'reviewer-override'
          : noReviewers
            ? 'no-reviewers-justification'
            : null;
        doc.stateHistory = [
          ...(doc.stateHistory || []),
          makeHistoryEntry({
            from: DOC_STATUS.IN_REVIEW,
            to: DOC_STATUS.READY_FOR_APPROVAL,
            by: session?.userId,
            comment: overrideComment || null,
            reason,
          }),
        ];
        result = { ...doc };
      });

      if (err) return err;
      if (result) {
        emitEngineEvent('document.ready_for_approval', { doc: result });
        if (session)
          logActivity(tenantId, session, {
            module: 'Documents',
            actionType: 'ready_for_approval',
            recordId: docId,
            recordLabel: result.title,
          });
      }
    },
    [updateState, tenantId]
  );

  const approveDocumentByOwner = useCallback(
    (docId, comment, session) => {
      let result = null;
      let err = null;
      let autoPublished = false;
      updateState((s) => {
        const doc = s.documents.find((d) => d.id === docId);
        if (!doc) return;

        const { isOwner, isWriter } = getDocRoles(doc, session?.userId);
        // I-17, unless an independent reviewer already signed the current cycle
        // off — a completed review IS the second pair of eyes I-17 asks for
        // (DOC-B-007). Mirrors documents-workflow's handleApprove.
        if (isOwner && isWriter && !independentReviewSatisfied(doc)) {
          err = 'ERR_OWNER_IS_WRITER';
          console.error('[document-workflow] owner-writer collision detected', {
            docId,
            userId: session?.userId,
          });
          return;
        }
        if (!canTransition(doc, 'approve', session?.userId)) {
          err = 'ERR_FORBIDDEN';
          return;
        }
        if (doc.ownerAbsenceTaskId) {
          err = 'ERR_OWNER_ABSENT';
          return;
        }

        const ts = now();
        doc.documentStatus = DOC_STATUS.APPROVED;
        doc.approvalRecord = {
          version: doc.version,
          approvedBy: session?.userId,
          approvedAt: ts,
          comment: comment || null,
        };
        doc.lastUpdated = ts;
        doc.approvalDecision = 'approved';
        doc.approvalComment = comment || null;
        doc.approvalDecidedAt = ts;

        doc.stateHistory = [
          ...(doc.stateHistory || []),
          makeHistoryEntry({
            from: DOC_STATUS.READY_FOR_APPROVAL,
            to: DOC_STATUS.APPROVED,
            by: session?.userId,
            comment: comment || null,
            reason: null,
          }),
        ];

        // Decision 3 (scoped to first publication): a document that has never
        // been published goes live the moment it is approved — no separate
        // publish click, no stranded "approved" state, no publish-pending
        // reminder. Revisions of an already-published document keep the
        // explicit publish step so the writer retains the Minor/Material
        // acknowledgement choice (everPublished may be unset on modern docs, so
        // publishedAt / publishedVersion are the authoritative markers).
        const firstPublication = !doc.everPublished && !doc.publishedAt && !doc.publishedVersion;
        if (firstPublication) {
          const publishedVer = bumpMajor(doc.version);
          doc.documentStatus = DOC_STATUS.PUBLISHED;
          doc.version = publishedVer;
          doc.publishedVersion = publishedVer;
          doc.materialVersion = publishedVer;
          doc.everPublished = true;
          doc.publishedAt = ts;
          doc.publishedBy = session?.userId;
          doc.effectiveFrom = ts;
          doc.draftInProgress = false;
          doc.readAcknowledgements = []; // first publication — nothing to preserve
          doc.stateHistory = [
            ...doc.stateHistory,
            makeHistoryEntry({
              from: DOC_STATUS.APPROVED,
              to: DOC_STATUS.PUBLISHED,
              by: session?.userId,
              reason: 'auto-publish-on-approval',
            }),
          ];
          autoPublished = true;
        }
        result = { ...doc };
      });

      if (err) return err;
      if (result) {
        emitEngineEvent('document.approved', { doc: result, decision: 'approved' });
        if (autoPublished) emitEngineEvent('document.published', { doc: result });
        if (session) {
          logActivity(tenantId, session, {
            module: 'Documents',
            actionType: 'approved',
            recordId: docId,
            recordLabel: result.title,
          });
          if (autoPublished)
            logActivity(tenantId, session, {
              module: 'Documents',
              actionType: 'published',
              recordId: docId,
              recordLabel: result.title,
            });
        }
      }
    },
    [updateState, tenantId]
  );

  const rejectDocumentByOwner = useCallback(
    (docId, comment, session) => {
      let result = null;
      let err = null;
      updateState((s) => {
        const doc = s.documents.find((d) => d.id === docId);
        if (!doc) return;
        if (!canTransition(doc, 'reject_by_owner', session?.userId)) {
          err = 'ERR_FORBIDDEN';
          return;
        }
        if (!comment?.trim()) {
          err = 'ERR_COMMENT_REQUIRED';
          return;
        }

        const ts = now();
        doc.documentStatus = DOC_STATUS.CHANGES_REQUESTED;
        doc.lastUpdated = ts;
        doc.approvalDecision = 'rejected';
        doc.approvalComment = comment;
        doc.approvalDecidedAt = ts;

        doc.stateHistory = [
          ...(doc.stateHistory || []),
          makeHistoryEntry({
            from: DOC_STATUS.READY_FOR_APPROVAL,
            to: DOC_STATUS.CHANGES_REQUESTED,
            by: session?.userId,
            comment,
            reason: 'owner-rejection',
          }),
        ];
        result = { ...doc };
      });

      if (err) return err;
      if (result) {
        emitEngineEvent('document.rejected', { doc: result });
        if (session)
          logActivity(tenantId, session, {
            module: 'Documents',
            actionType: 'rejected_by_owner',
            recordId: docId,
            recordLabel: result.title,
          });
      }
    },
    [updateState, tenantId]
  );

  const publishDocument = useCallback(
    (docId, session) => {
      let result = null;
      let err = null;
      updateState((s) => {
        const doc = s.documents.find((d) => d.id === docId);
        if (!doc) return;
        if (!canTransition(doc, 'publish', session?.userId)) {
          err = 'ERR_FORBIDDEN';
          return;
        }

        const ts = now();
        doc.documentStatus = DOC_STATUS.PUBLISHED;
        doc.publishedAt = ts;
        doc.publishedBy = session?.userId;
        doc.effectiveFrom = ts;
        doc.lastUpdated = ts;
        // The revision this publish puts in force is no longer a draft.
        doc.draftInProgress = false;

        doc.stateHistory = [
          ...(doc.stateHistory || []),
          makeHistoryEntry({
            from: DOC_STATUS.APPROVED,
            to: DOC_STATUS.PUBLISHED,
            by: session?.userId,
            reason: null,
          }),
        ];
        result = { ...doc };
      });

      if (err) return err;
      if (result) {
        emitEngineEvent('document.published', { doc: result });
        if (session)
          logActivity(tenantId, session, {
            module: 'Documents',
            actionType: 'published',
            recordId: docId,
            recordLabel: result.title,
          });
      }
    },
    [updateState, tenantId]
  );

  const publishDocumentDirect = useCallback(
    (docId, overrideComment, session) => {
      let result = null;
      let err = null;
      updateState((s) => {
        const doc = s.documents.find((d) => d.id === docId);
        if (!doc) return;

        // DE-02 (DOC-B-005): a direct publish is authorized only by an admin/
        // super-admin, or the document owner who is not also its writer (I-17).
        // A plain writer can no longer self-serve it. canTransition vouches for
        // the non-writer owner; admin is checked here where the role is known.
        if (doc.approvalRequired !== false) {
          err = 'ERR_FORBIDDEN';
          return;
        }
        const priorStatus = normalizeDocStatus(doc.documentStatus);
        if (priorStatus !== DOC_STATUS.DRAFT && priorStatus !== DOC_STATUS.IN_REVIEW) {
          err = 'ERR_WRONG_STATE';
          return;
        }
        const isAdmin = session?.role === 'admin' || session?.role === 'super-admin';
        if (!isAdmin && !canTransition(doc, 'publish_direct', session?.userId)) {
          err = 'ERR_FORBIDDEN';
          return;
        }

        if (priorStatus === DOC_STATUS.IN_REVIEW) {
          const quorumMet = reviewersQuorumSatisfied(doc);
          const noReviewers = (doc.reviewers || []).length === 0;
          if (!quorumMet && !noReviewers && !overrideComment?.trim()) {
            err = 'ERR_COMMENT_REQUIRED';
            return;
          }
          if ((!quorumMet || noReviewers) && overrideComment?.trim()) {
            doc.reviewerOverride = {
              overriddenBy: session?.userId,
              overriddenAt: now(),
              comment: overrideComment,
            };
          }
        }

        const ts = now();
        const fromStatus = priorStatus;
        doc.documentStatus = DOC_STATUS.PUBLISHED;
        doc.publishedAt = ts;
        doc.publishedBy = session?.userId;
        doc.effectiveFrom = ts;
        doc.lastUpdated = ts;
        // The revision this publish puts in force is no longer a draft.
        doc.draftInProgress = false;
        doc.approvalRecord = {
          version: doc.version,
          approvedBy: session?.userId || null,
          approvedByRole: session?.role || null,
          approvedAt: ts,
          comment:
            'approvalRequired=false — direct publish authorized by ' +
            (isAdmin ? 'admin' : 'document owner'),
        };

        doc.stateHistory = [
          ...(doc.stateHistory || []),
          makeHistoryEntry({
            from: fromStatus,
            to: DOC_STATUS.PUBLISHED,
            by: session?.userId,
            comment: overrideComment || null,
            reason: 'approval-not-required',
            extra: {
              reviewBypassed: fromStatus === DOC_STATUS.DRAFT,
              authorizedByRole: session?.role || null,
            },
          }),
        ];
        result = { ...doc };
      });

      if (err) return err;
      if (result) {
        emitEngineEvent('document.published', { doc: result, approvalBypassed: true });
        if (session)
          logActivity(tenantId, session, {
            module: 'Documents',
            actionType: 'published_direct',
            recordId: docId,
            recordLabel: result.title,
          });
      }
    },
    [updateState, tenantId]
  );

  const retractDocumentFromReview = useCallback(
    (docId, session) => {
      let result = null;
      updateState((s) => {
        const doc = s.documents.find((d) => d.id === docId);
        if (!doc || !canTransition(doc, 'retract', session?.userId)) return;

        const ts = now();
        doc.documentStatus = DOC_STATUS.DRAFT;
        doc.lastUpdated = ts;
        doc.stateHistory = [
          ...(doc.stateHistory || []),
          makeHistoryEntry({
            from: DOC_STATUS.IN_REVIEW,
            to: DOC_STATUS.DRAFT,
            by: session?.userId,
            reason: 'retraction',
          }),
        ];
        doc.approverId = null;
        doc.approvalRequestedAt = null;
        doc.approvalDecision = null;
        doc.approvalComment = null;
        doc.approvalDecidedAt = null;
        result = { ...doc };
      });

      if (result) {
        emitEngineEvent('document.retracted', { doc: result });
        if (session)
          logActivity(tenantId, session, {
            module: 'Documents',
            actionType: 'retracted',
            recordId: docId,
            recordLabel: result.title,
          });
      }
    },
    [updateState, tenantId]
  );

  const resubmitAfterChanges = useCallback(
    (docId, session) => {
      let result = null;
      updateState((s) => {
        const doc = s.documents.find((d) => d.id === docId);
        if (!doc || !canTransition(doc, 'resubmit_after_changes', session?.userId)) return;

        const ts = now();
        doc.documentStatus = DOC_STATUS.IN_REVIEW;
        doc.reviewDecisions = [];
        doc.reviewerEscalations = [];
        doc.reviewerOverride = null;
        doc.reviewCycleStartedAt = ts;
        doc.lastUpdated = ts;
        doc.approvalDecision = null;
        doc.approvalComment = null;

        doc.stateHistory = [
          ...(doc.stateHistory || []),
          makeHistoryEntry({
            from: DOC_STATUS.CHANGES_REQUESTED,
            to: DOC_STATUS.IN_REVIEW,
            by: session?.userId,
            reason: 'resubmit',
          }),
        ];
        result = { ...doc };
      });

      if (result) {
        emitEngineEvent('document.submitted_for_review', { doc: result });
        if (session)
          logActivity(tenantId, session, {
            module: 'Documents',
            actionType: 'resubmitted_for_review',
            recordId: docId,
            recordLabel: result.title,
          });
      }
    },
    [updateState, tenantId]
  );

  const startNewDocumentVersion = useCallback(
    (docId, session) => {
      let result = null;
      updateState((s) => {
        const doc = s.documents.find((d) => d.id === docId);
        if (!doc || !canTransition(doc, 'start_new_version', session?.userId)) return;

        const ts = now();
        const newVersion = bumpMinor(doc.version);

        doc.versionHistory = [
          ...(doc.versionHistory || []),
          {
            version: doc.version,
            updatedAt: ts,
            updatedBy: session?.userId,
            changes: [`New draft version ${newVersion} started`],
            approvalRecord: doc.approvalRecord ? { ...doc.approvalRecord } : null,
          },
        ];

        doc.documentStatus = DOC_STATUS.DRAFT;
        doc.version = newVersion;
        doc.draftInProgress = true;
        doc.approvalRecord = null;
        doc.reviewDecisions = [];
        doc.reviewerEscalations = [];
        doc.reviewerOverride = null;
        doc.reviewCycleStartedAt = null;
        doc.approvalRequiredLockedAt = null;
        doc.lastUpdated = ts;
        doc.approvalDecision = null;
        doc.approvalComment = null;
        doc.approvalDecidedAt = null;

        doc.stateHistory = [
          ...(doc.stateHistory || []),
          makeHistoryEntry({
            from: DOC_STATUS.PUBLISHED,
            to: DOC_STATUS.DRAFT,
            by: session?.userId,
            reason: 'new-version',
            extra: { newVersion },
          }),
        ];
        result = { ...doc };
      });

      if (result) {
        emitEngineEvent('document.new_version', { doc: result });
        if (session)
          logActivity(tenantId, session, {
            module: 'Documents',
            actionType: 'new_version_started',
            recordId: docId,
            recordLabel: result.title,
          });
      }
    },
    [updateState, tenantId]
  );

  const expireDocument = useCallback(
    (docId, comment, session) => {
      let result = null;
      let err = null;
      updateState((s) => {
        const doc = s.documents.find((d) => d.id === docId);
        if (!doc) return;
        if (normalizeDocStatus(doc.documentStatus) !== DOC_STATUS.PUBLISHED) {
          err = 'ERR_WRONG_STATE';
          return;
        }
        if (!comment?.trim()) {
          err = 'ERR_COMMENT_REQUIRED';
          return;
        }

        const ts = now();
        const taskId = generateId();
        doc.documentStatus = DOC_STATUS.EXPIRED;
        doc.renewalTaskId = taskId;
        doc.lastUpdated = ts;

        doc.stateHistory = [
          ...(doc.stateHistory || []),
          makeHistoryEntry({
            from: DOC_STATUS.PUBLISHED,
            to: DOC_STATUS.EXPIRED,
            by: session?.userId,
            comment,
            reason: 'manual-expiry',
            extra: { renewalTaskId: taskId },
          }),
        ];

        if (!s.adhocTasks) s.adhocTasks = [];
        s.adhocTasks.push({
          id: taskId,
          title: `Renew document: ${doc.title}`,
          description: `Document expired. A new approval cycle is required.`,
          status: 'open',
          priority: 'high',
          assignedTo: doc.writerId || doc.owner,
          createdAt: ts,
          dueDate: null,
          linkedDocId: docId,
          type: 'document-renewal',
        });
        result = { ...doc };
      });

      if (err) return err;
      if (result) {
        emitEngineEvent('document.expired', { doc: result });
        if (session)
          logActivity(tenantId, session, {
            module: 'Documents',
            actionType: 'expired',
            recordId: docId,
            recordLabel: result.title,
          });
      }
    },
    [updateState, tenantId]
  );

  // Marking a document obsolete is the only sanctioned way to unblock deletion
  // of an in_review/ready_for_approval/approved/published document (DOC-B-003)
  // and, like reassignDocumentOwner, must go through documents-workflow's
  // obsolete action (Owner | admin, server-enforced) rather than a local-only
  // state mutation — this document may have been read/written concurrently by
  // another tab, sweeper, or admin, and only the versioned per-document write
  // in documents-workflow is safe against that.
  const obsoleteDocument = useCallback(
    async (docId, comment, supersededBy, session) => {
      const result = await runVersionBumpingWrite(() =>
        callDocumentWorkflow(tenantId, 'obsolete', {
          docId,
          comment: comment || null,
          supersededBy: supersededBy || null,
        })
      );
      const serverDoc = result?.doc;
      if (!serverDoc) throw new Error('The workflow service returned no document.');

      const obsoletedDoc = adoptWorkflowDoc(docId, serverDoc);

      if (obsoletedDoc) emitEngineEvent('document.obsoleted', { doc: obsoletedDoc });

      if (session && obsoletedDoc) {
        logActivity(tenantId, session, {
          module: 'Documents',
          actionType: 'obsoleted',
          recordId: docId,
          recordLabel: obsoletedDoc.title,
        });
      }
      return obsoletedDoc;
    },
    [adoptWorkflowDoc, tenantId, runVersionBumpingWrite]
  );

  // SUP-07 (GH #7123): a periodic review that found nothing to revise. The
  // review event (who, when, the text reviewed) is written by documents-
  // workflow's record_review into doc.reviewLog — never here — and the
  // document comes back with its new nextReview.
  const recordDocumentReview = useCallback(
    async (docId, { nextReview, comment }, session) => {
      const result = await runVersionBumpingWrite(() =>
        callDocumentWorkflow(tenantId, 'record_review', {
          docId,
          nextReview,
          comment: comment || null,
        })
      );
      const serverDoc = result?.doc;
      if (!serverDoc) throw new Error('The workflow service returned no document.');
      const reviewedDoc = adoptWorkflowDoc(docId, serverDoc);
      if (session && reviewedDoc) {
        logActivity(tenantId, session, {
          module: 'Documents',
          actionType: 'review_completed',
          recordId: docId,
          recordLabel: reviewedDoc.title,
        });
      }
      return reviewedDoc;
    },
    [adoptWorkflowDoc, tenantId, runVersionBumpingWrite]
  );

  const applyMinorAmendment = useCallback(
    (docId, { contentAfter, comment }, session) => {
      let result = null;
      let err = null;
      updateState((s) => {
        const doc = s.documents.find((d) => d.id === docId);
        if (!doc) return;
        if (!canTransition(doc, 'apply_minor_amendment', session?.userId)) {
          err = 'ERR_FORBIDDEN';
          return;
        }
        if (!comment?.trim()) {
          err = 'ERR_COMMENT_REQUIRED';
          return;
        }

        const ts = now();
        const amendmentId = generateId();

        doc.minorAmendments = [
          ...(doc.minorAmendments || []),
          {
            id: amendmentId,
            appliedAt: ts,
            appliedBy: session?.userId,
            comment,
            contentAfter: contentAfter || null,
          },
        ];
        doc.lastUpdated = ts;
        result = { ...doc };
      });

      if (err) return err;
      if (result) {
        emitEngineEvent('document.minor_amendment', { doc: result });
        if (session)
          logActivity(tenantId, session, {
            module: 'Documents',
            actionType: 'minor_amendment',
            recordId: docId,
            recordLabel: result.title,
          });
      }
    },
    [updateState, tenantId]
  );

  // Approver (owner) reassignment: use reassignDocumentOwner above, which
  // wraps documents-workflow's admin-gated reassign_owner action (DOC-B-006).
  // callWorkflow below is the generic escape hatch for other workflow
  // actions that don't yet have a dedicated wrapper.

  // Routed through runVersionBumpingWrite like every dedicated wrapper above:
  // documents-workflow rewrites tenant_state on a transition, so an escape
  // hatch that skipped the version claim would reintroduce GH #5314 for
  // whichever action reached for it next.
  const callWorkflow = useCallback(
    async (action, body) => {
      const result = await runVersionBumpingWrite(() =>
        callDocumentWorkflow(tenantId, action, body)
      );
      // Adopt the transition's own result here too. Without it the escape
      // hatch — which is what DocumentWorkflowHub's submit / reviewer-decision
      // / approve / publish buttons all go through — left local state holding
      // the PRE-transition document and, worse, its pre-transition row version
      // (GH #6672). An editor open on that document then sent a version the
      // server had already moved past on its next content save, and the writer
      // was asked to resolve a conflict nobody had caused.
      adoptWorkflowDoc(body && body.docId, result?.doc);
      return result;
    },
    [tenantId, runVersionBumpingWrite, adoptWorkflowDoc]
  );

  return {
    saveDocument,
    callWorkflow,
    // A document another endpoint wrote on this tab's behalf (an accepted
    // document-scan suggestion, control-doc-scans): adopt it and its row
    // version exactly as a workflow transition's result.
    adoptServerDocument: adoptWorkflowDoc,
    deleteDocument,
    retractDocument,
    saveDocumentApproval,
    submitDocumentForReview,
    submitReviewerDecision,
    advanceToReadyForApproval,
    approveDocumentByOwner,
    rejectDocumentByOwner,
    reassignDocumentOwner,
    publishDocument,
    publishDocumentDirect,
    retractDocumentFromReview,
    resubmitAfterChanges,
    startNewDocumentVersion,
    expireDocument,
    obsoleteDocument,
    recordDocumentReview,
    applyMinorAmendment,
  };
}
